import React, { useState, useEffect } from "react"
import { FormattedMessage } from "react-intl"
import Row from "react-bootstrap/Row"
import Col from "react-bootstrap/Col"
import Container from "react-bootstrap/Container"
import Card from "react-bootstrap/Card"
import InputGroup from "react-bootstrap/InputGroup"
import { Button } from "react-bootstrap"
import { Diagram3, Calendar, Calendar2Date, HandThumbsUp, Download, Upload, PeopleFill, ChevronLeft, ChevronRight } from "react-bootstrap-icons"

import formatCurrency from "../helpers/currencyHelper"
import SearchSelect from "../components/form/SearchSelect"
import ProjectService from "../services/ProjectsService"
import PaymentsPartnersReports from "./reports/PaymentsPartnersReports"
import ExpensesProjectsReports from "./reports/ExpensesProjectsReports"
import IncomesReports from "./reports/IncomesReports"
import Projects from "./Projects"
import Incomes from "./Incomes"
import ExpensesProjects from "./ExpensesProjects"
import PartnersService from "../services/PartnersService"
import { MAX_YEAR, MIN_YEAR } from "../config/calendar"

const ReportsProjects = () => {
    const currentYear = new Date().getFullYear()
    const [selectedYear, setSelectedYear] = useState(currentYear)

    const [incomesTotalAmount, setIncomesTotalAmount] = useState(0)
    const [expensesProjectsTotalAmount, setExpensesProjectsTotalAmount] = useState(0)
    const [paymentsPartnersTotalAmount, setPaymentsPartnersTotalAmount] = useState(0)

    const [selectedProject, setSelectedProject] = useState("")
    const [selectedPartner, setSelectedPartner] = useState("")

    const [projectsList, setProjectsList] = useState([])
    const [partnersList, setPartnersList] = useState([])

    const handleYearPrevious = () => {
        if (selectedYear > MIN_YEAR) {
            setSelectedYear(selectedYear - 1)
        }
    }

    const handleYearNext = () => {
        if (selectedYear < MAX_YEAR) {
            setSelectedYear(selectedYear + 1)
        }
    }   

    const fetchProjects = async () => {
        try {
            const response = await ProjectService.getProjects(1, "", 2000)
            setProjectsList(response.data.projects)
        } catch (err) {
            console.error("Error fetching projects:", err)
        }
    }

    const fetchPartners = async () => {
        try {
            const response = await PartnersService.getPartners(1, "", 2000)
            setPartnersList(response.data.partners)
        } catch (err) {
            console.error("Error fetching partners:", err)
        }
    }

    useEffect(() => {
        fetchProjects()
        fetchPartners()
    }, [])

    useEffect(() => {
        // Resetear totales cuando cambian los filtros o el año
        setIncomesTotalAmount(0)
        setExpensesProjectsTotalAmount(0)
        setPaymentsPartnersTotalAmount(0)
    }, [selectedYear, selectedProject, selectedPartner])

    return (
        <Container className="p-3" fluid>

            <Row className="p-1 bg-secondary">
                {/* Selector de Proyecto */}
                <Col xs={6}>
                    <InputGroup>
                        <InputGroup.Text><Diagram3 /></InputGroup.Text>
                        <SearchSelect
                            fieldName="projectId"
                            selected={selectedProject}
                            onChange={(e) => setSelectedProject(e.target.value)}
                            options={projectsList.map(proj => ({ id: proj._id, name: proj.name }))}
                        />
                    </InputGroup>
                </Col>

                {/* Selector de Partner */}
                <Col xs={6}>
                    <InputGroup>
                        <InputGroup.Text><HandThumbsUp /></InputGroup.Text>
                        <SearchSelect
                            fieldName="partnerId"
                            selected={selectedPartner}
                            onChange={(e) => setSelectedPartner(e.target.value)}
                            options={partnersList}
                        />
                    </InputGroup>
                </Col>
            </Row>

            {/**Selector año */}
            <Row className="mb-4 mt-4 justify-content-center">
                <Col xs="auto">
                    <InputGroup>
                        <Button
                            variant="outline-secondary"
                            onClick={handleYearPrevious}
                            disabled={selectedYear <= MIN_YEAR}
                        >
                            <ChevronLeft />
                        </Button>
                        <InputGroup.Text className="bg-white border-start-0 border-end-0 fw-bold">
                            {selectedYear}
                        </InputGroup.Text>
                        <Button
                            variant="outline-secondary"
                            onClick={handleYearNext}
                            disabled={selectedYear >= MAX_YEAR}
                        >
                            <ChevronRight />
                        </Button>
                    </InputGroup>
                </Col>
            </Row>

            <Row className="mb-3 mt-3">
                {/* Reporte de Ingresos */}
                <Col xs={6} lg={6} className="mb-4 mb-lg-0">
                    <Card className="shadow-sm border-1">
                        <Card.Header className="bg-white d-flex justify-content-between align-items-center border-0 pb-0">
                            <div className="d-flex align-items-center fw-bold">
                                <Download
                                    className="text-success me-2"
                                    size={20}
                                    strokeWidth={4.5}
                                />
                                <span className="fs-5 fw-bold">
                                    <FormattedMessage id="navProjectIncomes" defaultMessage="Projects Incomes" />:
                                </span>
                            </div>
                            <div className="fs-5 fw-bold text-success">
                                {formatCurrency(incomesTotalAmount)}
                            </div>
                        </Card.Header>
                        <Card.Body className="pt-2">
                            <hr className="my-2 border border-secondary" />
                            <div className="p-3 rounded bg-light">
                                <IncomesReports
                                    yearFrom={selectedYear}
                                    yearTo={selectedYear}
                                    projectId={selectedProject}
                                    partnerId={selectedPartner}
                                    onChange={(option) => setSelectedProject(option?.id || "")}
                                    onChangeDataset={(dataSet) => setIncomesTotalAmount(dataSet.totalAmountsSum)}
                                />
                            </div>
                        </Card.Body>
                    </Card>
                </Col>

                {/* Reporte de Gastos por Proyecto */}
                <Col xs={6} lg={6} className="mb-4 mb-lg-0">
                    <Card className="shadow-sm border-1">
                        <Card.Header className="bg-white d-flex justify-content-between align-items-center border-0 pb-0">
                            <div className="d-flex align-items-center fw-bold">
                                <Upload
                                    className="text-danger me-2"
                                    size={20}
                                    strokeWidth={4.5}
                                />
                                <span className="fs-5 fw-bold">
                                    <FormattedMessage id="navExpensesProjects" defaultMessage="Projects Expenses" />:
                                </span>
                            </div>
                            <div className="fs-5 fw-bold text-danger">
                                {formatCurrency(expensesProjectsTotalAmount)}
                            </div>
                        </Card.Header>
                        <Card.Body className="pt-2">
                            <hr className="my-2 border border-secondary" />
                            <div className="p-3 rounded bg-light">
                                <ExpensesProjectsReports
                                    yearFrom={selectedYear}
                                    yearTo={selectedYear}
                                    projectId={selectedProject}
                                    partnerId={selectedPartner}
                                    onChange={(option) => setSelectedProject(option?.id || "")}
                                    onChangeDataset={(dataSet) => setExpensesProjectsTotalAmount(dataSet.totalAmountsSum)}
                                />
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <Row className="mb-3 mt-3">
                <Col xs={3} lg={3} className="mb-4 mb-lg-0"></Col>
                {/* Reporte de Pagos a Socios */}
                <Col xs={6} lg={6} className="mb-4 mb-lg-0">
                    <Card className="shadow-sm border-1">
                        <Card.Header className="bg-white d-flex justify-content-between align-items-center border-0 pb-0">
                            <div className="d-flex align-items-center fw-bold">
                                <PeopleFill
                                    className="text-info me-2"
                                    size={20}
                                    strokeWidth={4.5}
                                />
                                <span className="fs-5 fw-bold">
                                    <FormattedMessage id="navPaymentsPartners" defaultMessage="Partners Payments" />:
                                </span>
                            </div>
                            <div className="fs-5 fw-bold text-info">
                                {formatCurrency(paymentsPartnersTotalAmount)}
                            </div>
                        </Card.Header>
                        <Card.Body className="pt-2">
                            <hr className="my-2 border border-secondary" />
                            <div className="p-3 rounded bg-light">
                                <PaymentsPartnersReports
                                    yearFrom={selectedYear}
                                    yearTo={selectedYear}
                                    projectId={selectedProject}
                                    partnerId={selectedPartner}
                                    onChange={(option) => setSelectedProject(option?.id || "")}
                                    onChangeDataset={(dataSet) => setPaymentsPartnersTotalAmount(dataSet.totalAmountsSum)}
                                />
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
                <Col xs={3} lg={3} className="mb-4 mb-lg-0"></Col>
            </Row>

            <hr className="border border-dark mt-5" />

            {!selectedProject && !selectedPartner && (
                <>
                    <Row className="mt-5">
                        <Projects showSearchBar={false} />
                    </Row>

                    <hr className="border border-dark mt-5" />
                </>
            )}

            <Row className="mt-5">
                <Incomes showSearchBar={false} selectedProject={selectedProject} selectedPartner={selectedPartner} yearFrom={selectedYear} yearTo={selectedYear} />
            </Row>

            <hr className="border border-dark mt-5" />

            <Row className="mt-5">
                <ExpensesProjects showSearchBar={false} selectedProject={selectedProject} yearFrom={selectedYear} yearTo={selectedYear} />
            </Row>
        </Container>
    )
}

export default ReportsProjects